import { Globals as CoreGlobals } from '@core/globals'
import { DomainConfig } from '@core/types/domain-config.ts'
import { PromptGroup } from './types/prompts.ts'
import { Env } from './env.ts'

/** @internal */
export class Globals extends CoreGlobals {
    static get _dynamicDomainKey(): string | null {
        return (
            Globals._injectedDomainKey ??
            Globals._injectedDomainConfig?.domain_key ??
            Globals._requestingScriptDomainKey
        )
    }

    static get _requestingScriptDomainKey(): string | null {
        try {
            const params = new URLSearchParams(document.currentScript?.getAttribute('src')?.split('?')[1] ?? '')
            return params.get('domain_key') ?? params.get('domainKey')
        } catch {}

        return null
    }

    static get _udrUrlSearchParameter() {
        return 'pushly_udr' as const
    }

    // region Preview

    /**
     * Whether this page load should ignore any stored settings copy and hydrate from remote.
     *
     * Enabled by `?pn_preview=1` on the current URL or by a `_pn_preview` cookie. The cookie
     * exists for verifying a flow that spans several pages, where threading a query parameter
     * through every link is impractical.
     *
     * Evaluated per page load and never persisted by the SDK, so preview cannot leak into an
     * end user's session: a publisher opts in explicitly and it lapses on their next
     * navigation unless they opted in via the cookie.
     */
    static get _isPreviewModeEnabled(): boolean {
        return this._isPreviewEnabledByUrl || this._isPreviewEnabledByCookie
    }

    private static get _isPreviewEnabledByUrl(): boolean {
        try {
            const params = new URLSearchParams(Env._location.search)
            return this._isPreviewOptInValue(params.get(this._pnPreviewUrlSearchParameter))
        } catch {
            return false
        }
    }

    private static get _isPreviewEnabledByCookie(): boolean {
        try {
            const cookies = Env._document.cookie.split(';')

            for (const cookie of cookies) {
                const separatorIndex = cookie.indexOf('=')
                if (separatorIndex === -1) {
                    continue
                }

                if (cookie.slice(0, separatorIndex).trim() !== this._pnPreviewCookieName) {
                    continue
                }

                return this._isPreviewOptInValue(decodeURIComponent(cookie.slice(separatorIndex + 1).trim()))
            }
        } catch {}

        return false
    }

    /**
     * Treats an explicitly negative value as opting out so `?pn_preview=0` reads the way
     * anyone would expect rather than enabling preview on the strength of the key existing.
     */
    private static _isPreviewOptInValue(value: string | null): boolean {
        const normalized = value?.trim().toLowerCase()
        return !!normalized && normalized !== '0' && normalized !== 'false'
    }

    // endregion

    // region Flags

    static get _swUseRootScopeFlag() {
        return 'USE_ROOT_SW_SCOPE' as const
    }

    static get _swUsePathWithoutDomainKeyFlag() {
        return 'SW_NO_DOMAIN_KEY_PARAM' as const
    }

    static get _swShouldReplaceExistingWorkerInPushlyScopeFlag() {
        return 'REPLACE_EXISTING_WORKERS' as const
    }

    static get _swShouldUnregisterNonPushlyWorkersFlag() {
        return 'UNREGISTER_ALL_NON_PUSHLY_WORKERS' as const
    }

    /**
     * Kill switch (NS-2554): when present, the SDK stops unregistering duplicate pushly
     * service workers. The cleanup is ON by default (absent this flag) so it reaches every
     * domain; adding this flag to a domain disables the `unregister()` of duplicate workers
     * instantly via config — no SDK re-release — should the duplicate-collapse ever orphan a
     * live endpoint. The keeper selection still runs; only the destructive unregister is gated.
     */
    static get _swDisableDuplicateWorkerCleanupFlag() {
        return 'DISABLE_DUPLICATE_SW_CLEANUP' as const
    }

    static get _domainFeatIntegrationsECommFlag() {
        return 'FEAT_DOMAIN_INTEGRATIONS_ECOMM' as const
    }

    static get _domainFeatUseIsolatedCookiesFlag() {
        return 'USE_ISOLATED_COOKIE_STORAGE_LOCATION' as const
    }

    // endregion

    // region Storage
    // region Cache

    static get _defaultDismissedStatusExpirationSeconds() {
        // 7 Days
        return 604800 as const
    }

    // endregion
    // endregion

    // region Service Worker

    /**
     * Ceiling, in milliseconds, on how long `_synchronize` waits on
     * `navigator.serviceWorker.ready` for an already-subscribed user's existing
     * service worker to activate before reading its subscription (and, if still
     * absent, re-subscribing). The wait is event-driven and resolves the instant the
     * worker activates — this only bounds the pathological case where no registration
     * exists so `ready` would otherwise never resolve. The shorter `_ready(200)` used
     * for the general path stays in place so first-time visitors aren't blocked (NS-2554).
     */
    static get _coldSubscriptionResolutionBudgetMs() {
        return 5000 as const
    }

    // endregion

    // region Injected Resources

    static get _injectedDomainKey(): string | null {
        try {
            return !self._PN_IDK_ || typeof self._PN_IDK_ !== 'string' ? null : self._PN_IDK_
        } catch {
            return null
        }
    }

    static get _injectedDomainConfig(): DomainConfig | null {
        try {
            return !self._PN_IDC_ ||
                typeof self._PN_IDC_ !== 'object' ||
                Array.isArray(self._PN_IDC_) ||
                !('domain_key' in self._PN_IDC_)
                ? null
                : (self._PN_IDC_ as DomainConfig)
        } catch {
            return null
        }
    }

    static get _injectedPromptGroups(): PromptGroup[] | null {
        try {
            return !self._PN_IPG_ ||
                !Array.isArray(self._PN_IPG_) ||
                !self._PN_IPG_.length ||
                !self._PN_IPG_.every((g) => 'prompts' in g)
                ? null
                : (self._PN_IPG_ as PromptGroup[])
        } catch {
            return null
        }
    }

    // endregion

    // region Events
    // region Data keys

    static get _tevSubscribedUrlKey() {
        return 'subscribed_url' as const
    }

    static get _tevDataPageUrlKey() {
        return 'page_url' as const
    }

    static get _tevDataPageTagsKey() {
        return 'page_tags' as const
    }

    static get _tevDataPromptNotShownKey() {
        return 'prompt_not_shown_reason' as const
    }
    // endregion
    // endregion
}
